/**
 * Local Database using Dexie (IndexedDB wrapper)
 * Provides structured data storage for all inventory items
 */
import Dexie from 'dexie';

// Create database
const db = new Dexie('ArmsRoomDB');

// Define schema
db.version(1).stores({
  firearms: 'id, manufacturer, model, type, caliber, created_at, updated_at',
  ammunition: 'id, caliber, brand, type, created_at, updated_at',
  accessories: 'id, name, type, brand, created_at, updated_at',
  documents: 'id, name, category, linked_firearm_id, linked_accessory_id, created_at, updated_at',
  wishlist: 'id, name, category, priority, created_at, updated_at',
  files: 'id, entityType, entityId, mimeType, created_at',
  settings: 'key',
  ammoLog: 'id, ammo_id, created_at'
});

// Helper to generate IDs
export const generateId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Helper to get current timestamp
export const timestamp = () => new Date().toISOString();

/**
 * Ask the browser to keep our data even under storage pressure.
 * Without this, browsers may evict IndexedDB (Safari can clear it after ~7 days
 * of non-use for sites not added to the home screen).
 * @returns {Promise<boolean|null>} true if persisted, false if denied, null if unsupported
 */
export const requestPersistentStorage = async () => {
  if (!navigator.storage?.persist) return null;
  try {
    if (await navigator.storage.persisted()) return true;
    const granted = await navigator.storage.persist();
    console.log('[LocalDB] Persistent storage', granted ? 'granted' : 'not granted');
    return granted;
  } catch (error) {
    console.error('[LocalDB] Persistent storage request failed:', error);
    return null;
  }
};

/**
 * Wipe all data from the database
 * Clears all tables while keeping the database structure intact
 * This is the proper way to wipe data without needing to delete/recreate the DB
 */
export const wipeAllData = async () => {
  console.log('[LocalDB] Starting data wipe...');
  try {
    // Clear all tables
    await db.transaction('rw', db.firearms, db.ammunition, db.accessories, db.documents, db.wishlist, db.files, db.settings, db.ammoLog, async () => {
      await db.firearms.clear();
      await db.ammunition.clear();
      await db.accessories.clear();
      await db.documents.clear();
      await db.wishlist.clear();
      await db.files.clear();
      await db.settings.clear();
      await db.ammoLog.clear();
    });
    console.log('[LocalDB] All tables cleared successfully');
    return true;
  } catch (error) {
    console.error('[LocalDB] Wipe error:', error);
    // If transaction fails, try clearing tables individually
    try {
      await db.firearms.clear();
      await db.ammunition.clear();
      await db.accessories.clear();
      await db.documents.clear();
      await db.wishlist.clear();
      await db.files.clear();
      await db.settings.clear();
      await db.ammoLog.clear();
      console.log('[LocalDB] All tables cleared (fallback method)');
      return true;
    } catch (fallbackError) {
      console.error('[LocalDB] Fallback wipe error:', fallbackError);
      throw fallbackError;
    }
  }
};


// ==================== FIREARMS ====================

export const firearmsCrud = {
  async getAll(filters = {}) {
    let collection = db.firearms.toCollection();
    let results = await collection.toArray();
    
    // Apply filters
    if (filters.search) {
      const search = filters.search.toLowerCase();
      results = results.filter(f => 
        f.manufacturer?.toLowerCase().includes(search) ||
        f.model?.toLowerCase().includes(search) ||
        f.caliber?.toLowerCase().includes(search) ||
        f.serial_number?.toLowerCase().includes(search)
      );
    }
    if (filters.type && filters.type !== 'all') {
      results = results.filter(f => f.type === filters.type);
    }
    if (filters.caliber && filters.caliber !== 'all') {
      results = results.filter(f => f.caliber === filters.caliber);
    }
    if (filters.tag && filters.tag !== 'all') {
      results = results.filter(f => f.tags?.includes(filters.tag));
    }
    
    // Sort by created_at descending
    results.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    return results;
  },
  
  async getById(id) {
    return await db.firearms.get(id);
  },
  
  async create(data) {
    const now = timestamp();
    const firearm = {
      id: generateId('firearm'),
      ...data,
      user_id: 'local_user',
      images: data.images || [],
      tags: data.tags || [],
      created_at: now,
      updated_at: now
    };
    await db.firearms.add(firearm);
    return firearm;
  },
  
  async update(id, data) {
    const existing = await db.firearms.get(id);
    if (!existing) throw new Error('Firearm not found');
    
    const updated = {
      ...existing,
      ...data,
      updated_at: timestamp()
    };
    await db.firearms.put(updated);
    return updated;
  },
  
  async delete(id) {
    await db.firearms.delete(id);
    // Also delete associated files
    await db.files.where({ entityType: 'firearm', entityId: id }).delete();
    return { message: 'Firearm deleted' };
  },
  
  async count() {
    return await db.firearms.count();
  },
  
  async getRecent(limit = 3) {
    const all = await this.getAll();
    return all.slice(0, limit);
  }
};

// ==================== AMMUNITION ====================

export const ammunitionCrud = {
  async getAll(filters = {}) {
    let results = await db.ammunition.toArray();
    
    if (filters.search) {
      const search = filters.search.toLowerCase();
      results = results.filter(a => 
        a.caliber?.toLowerCase().includes(search) ||
        a.brand?.toLowerCase().includes(search)
      );
    }
    if (filters.caliber && filters.caliber !== 'all') {
      results = results.filter(a => a.caliber === filters.caliber);
    }
    if (filters.type && filters.type !== 'all') {
      results = results.filter(a => a.type === filters.type);
    }
    
    // Sort by caliber
    results.sort((a, b) => (a.caliber || '').localeCompare(b.caliber || ''));
    
    return results;
  },
  
  async getById(id) {
    return await db.ammunition.get(id);
  },
  
  async create(data) {
    const now = timestamp();
    const ammo = {
      id: generateId('ammo'),
      ...data,
      user_id: 'local_user',
      rounds: data.rounds || 0,
      images: data.images || [],
      created_at: now,
      updated_at: now
    };
    await db.ammunition.add(ammo);
    return ammo;
  },
  
  async update(id, data) {
    const existing = await db.ammunition.get(id);
    if (!existing) throw new Error('Ammunition not found');
    
    const updated = {
      ...existing,
      ...data,
      updated_at: timestamp()
    };
    await db.ammunition.put(updated);
    return updated;
  },
  
  async adjust(id, amount) {
    const existing = await db.ammunition.get(id);
    if (!existing) throw new Error('Ammunition not found');
    
    const newRounds = Math.max(0, (existing.rounds || 0) + amount);
    const now = timestamp();
    
    const updated = {
      ...existing,
      rounds: newRounds,
      updated_at: now
    };
    await db.ammunition.put(updated);
    
    // Log the adjustment
    await db.ammoLog.add({
      id: generateId('log'),
      ammo_id: id,
      user_id: 'local_user',
      adjustment: amount,
      new_total: newRounds,
      created_at: now
    });
    
    return updated;
  },
  
  async delete(id) {
    await db.ammunition.delete(id);
    await db.files.where({ entityType: 'ammunition', entityId: id }).delete();
    return { message: 'Ammunition deleted' };
  },
  
  async getTotalRounds() {
    const all = await db.ammunition.toArray();
    return all.reduce((sum, a) => sum + (a.rounds || 0), 0);
  },
  
  async count() {
    return await db.ammunition.count();
  },
  
  async getRecent(limit = 3) {
    let results = await db.ammunition.toArray();
    results.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return results.slice(0, limit);
  }
};

// ==================== ACCESSORIES ====================

export const accessoriesCrud = {
  async getAll(filters = {}) {
    let results = await db.accessories.toArray();
    
    if (filters.search) {
      const search = filters.search.toLowerCase();
      results = results.filter(a => 
        a.name?.toLowerCase().includes(search) ||
        a.brand?.toLowerCase().includes(search)
      );
    }
    if (filters.type && filters.type !== 'all') {
      results = results.filter(a => a.type === filters.type);
    }
    if (filters.firearm_id) {
      results = results.filter(a => a.compatible_firearms?.includes(filters.firearm_id));
    }
    
    results.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    return results;
  },
  
  async getById(id) {
    return await db.accessories.get(id);
  },
  
  async create(data) {
    const now = timestamp();
    const accessory = {
      id: generateId('accessory'),
      ...data,
      user_id: 'local_user',
      images: data.images || [],
      compatible_firearms: data.compatible_firearms || [],
      created_at: now,
      updated_at: now
    };
    await db.accessories.add(accessory);
    return accessory;
  },
  
  async update(id, data) {
    const existing = await db.accessories.get(id);
    if (!existing) throw new Error('Accessory not found');
    
    const updated = {
      ...existing,
      ...data,
      updated_at: timestamp()
    };
    await db.accessories.put(updated);
    return updated;
  },
  
  async delete(id) {
    await db.accessories.delete(id);
    await db.files.where({ entityType: 'accessory', entityId: id }).delete();
    return { message: 'Accessory deleted' };
  },
  
  async count() {
    return await db.accessories.count();
  },
  
  async getRecent(limit = 3) {
    const all = await this.getAll();
    return all.slice(0, limit);
  }
};

// ==================== DOCUMENTS ====================

export const documentsCrud = {
  async getAll(filters = {}) {
    let results = await db.documents.toArray();
    
    if (filters.search) {
      const search = filters.search.toLowerCase();
      results = results.filter(d => d.name?.toLowerCase().includes(search));
    }
    if (filters.category && filters.category !== 'all') {
      results = results.filter(d => d.category === filters.category);
    }
    if (filters.firearm_id) {
      results = results.filter(d => d.linked_firearm_id === filters.firearm_id);
    }
    if (filters.accessory_id) {
      results = results.filter(d => d.linked_accessory_id === filters.accessory_id);
    }
    
    results.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    // Return without file_data for list view
    return results.map(({ fileId, ...rest }) => rest);
  },
  
  async getById(id) {
    const doc = await db.documents.get(id);
    if (!doc) return null;
    
    // If there's a fileId, get the file data
    if (doc.fileId) {
      const file = await db.files.get(doc.fileId);
      if (file && file.blob) {
        // Convert blob to base64 for compatibility with existing UI
        const base64 = await blobToBase64(file.blob);
        return { ...doc, file_data: base64 };
      }
    }
    return doc;
  },
  
  async create(data) {
    const now = timestamp();
    let fileId = null;
    
    // If file_data (base64) is provided, convert to blob and store
    if (data.file_data) {
      const blob = base64ToBlob(data.file_data, data.file_type);
      fileId = generateId('file');
      await db.files.add({
        id: fileId,
        entityType: 'document',
        entityId: null, // Will update after document is created
        blob: blob,
        mimeType: getMimeType(data.file_type),
        created_at: now
      });
    }
    
    const document = {
      id: generateId('doc'),
      name: data.name,
      category: data.category,
      linked_firearm_id: data.linked_firearm_id || null,
      linked_accessory_id: data.linked_accessory_id || null,
      file_type: data.file_type,
      notes: data.notes || null,
      fileId: fileId,
      user_id: 'local_user',
      created_at: now,
      updated_at: now
    };
    
    await db.documents.add(document);
    
    // Update file with entityId
    if (fileId) {
      await db.files.update(fileId, { entityId: document.id });
    }
    
    // Return without file_data for response
    const { fileId: _, ...response } = document;
    return response;
  },
  
  async update(id, data) {
    const existing = await db.documents.get(id);
    if (!existing) throw new Error('Document not found');
    
    const updated = {
      ...existing,
      name: data.name ?? existing.name,
      category: data.category ?? existing.category,
      linked_firearm_id: data.linked_firearm_id ?? existing.linked_firearm_id,
      linked_accessory_id: data.linked_accessory_id ?? existing.linked_accessory_id,
      notes: data.notes ?? existing.notes,
      updated_at: timestamp()
    };
    await db.documents.put(updated);
    
    const { fileId, ...response } = updated;
    return response;
  },
  
  async delete(id) {
    const doc = await db.documents.get(id);
    if (doc?.fileId) {
      await db.files.delete(doc.fileId);
    }
    await db.documents.delete(id);
    return { message: 'Document deleted' };
  },
  
  async count() {
    return await db.documents.count();
  }
};

// ==================== WISHLIST ====================

export const wishlistCrud = {
  async getAll(filters = {}) {
    let results = await db.wishlist.toArray();
    
    if (filters.search) {
      const search = filters.search.toLowerCase();
      results = results.filter(w => w.name?.toLowerCase().includes(search));
    }
    if (filters.category && filters.category !== 'all') {
      results = results.filter(w => w.category === filters.category);
    }
    if (filters.priority && filters.priority !== 'all') {
      results = results.filter(w => w.priority === filters.priority);
    }
    
    results.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    return results;
  },
  
  async getById(id) {
    return await db.wishlist.get(id);
  },
  
  async create(data) {
    const now = timestamp();
    const item = {
      id: generateId('wish'),
      ...data,
      user_id: 'local_user',
      images: data.images || [],
      created_at: now,
      updated_at: now
    };
    await db.wishlist.add(item);
    return item;
  },
  
  async update(id, data) {
    const existing = await db.wishlist.get(id);
    if (!existing) throw new Error('Wishlist item not found');
    
    const updated = {
      ...existing,
      ...data,
      updated_at: timestamp()
    };
    await db.wishlist.put(updated);
    return updated;
  },
  
  async delete(id) {
    await db.wishlist.delete(id);
    await db.files.where({ entityType: 'wishlist', entityId: id }).delete();
    return { message: 'Wishlist item deleted' };
  },
  
  async count() {
    return await db.wishlist.count();
  }
};

// ==================== SETTINGS ====================

export const settingsCrud = {
  async get(key) {
    const setting = await db.settings.get(key);
    return setting?.value;
  },
  
  async set(key, value) {
    await db.settings.put({ key, value });
    return { key, value };
  },
  
  async getAll() {
    const settings = await db.settings.toArray();
    return settings.reduce((acc, s) => ({ ...acc, [s.key]: s.value }), {});
  }
};

// ==================== HELPERS ====================

function base64ToBlob(base64, fileType) {
  // Handle data URL format
  let base64Data = base64;
  let mimeType = getMimeType(fileType);
  
  if (base64.startsWith('data:')) {
    const parts = base64.split(',');
    const mimeMatch = parts[0].match(/:(.*?);/);
    if (mimeMatch) mimeType = mimeMatch[1];
    base64Data = parts[1];
  }
  
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function getMimeType(fileType) {
  const types = {
    'PDF': 'application/pdf',
    'JPEG': 'image/jpeg',
    'JPG': 'image/jpeg',
    'PNG': 'image/png',
    'WEBP': 'image/webp'
  };
  return types[fileType?.toUpperCase()] || 'application/octet-stream';
}

// Export database instance for advanced usage
export default db;
